import { v4 as uuid } from "uuid";
import * as yup from "yup";

import { Thumbnail, createThumbnail } from "./Thumbnail";
import { TFunction } from "i18next";
import {
  UploadFile,
  UploadFileValidation,
  getUploadFileObjectURL,
} from "./UploadFile";
import { stringOrEmpty } from "../commons/utils";
import { dateFromDicomString } from "../commons/dicom";
import moment from "moment";
import { Patient, patientStudy } from "./Patient";

/**
 * Wrapper to handle related files within the upload.
 * Resembles a DICOM series.
 */
export interface UploadStack {
  /** Stack ID
   * DICOM: Series Instance UID (0020,000E)
   * Media: UUID (V4)
   */
  id: string;
  /** MIME type of the stack */
  type: string;
  /** DICOM Flag - true, if stack is a DICOM Series. */
  dicom: boolean;
  /** Study Instance UID (0020,000D - DICOM only) */
  study: string;
  /** DICOM Modality (0008,0060) */
  modality: string;
  /** A description. DICOM: Series Description (0008,103E) */
  description: string;
  /** Series Date (0008,0021) */
  date: Date;
  /** Key image within stack to be used as preview/icon */
  keyImage: number;
  /** Stack's files */
  files: UploadFile[];
}

/** Form validation schema for a stack */
export const UploadStackValidation = (t: TFunction) =>
  yup.object({
    id: yup.string().required(),
    type: yup.string(),
    dicom: yup.boolean(),
    study: yup.string(),
    modality: yup.string(),
    date: yup.date(),
    description: yup.string(),
    keyImage: yup.number(),
    files: yup.array().of(UploadFileValidation),
  });

/**
 * Create an upload stack
 * @param id The stack ID.
 * @param type The MIME type. Default: ""
 * @param dicom DICOM flag. Default: false
 * @param modality DICOM Modality. Default: "OT"
 * @param date Series Date. Default: current date
 * @param description Series description. Default: ""
 * @param keyImage The stack's key image. Default: 0
 * @param files The files. Default: []
 */
export const createUploadStack = (
  id: string,
  type: string = "",
  dicom: boolean = false,
  study: string = "",
  modality: string = "OT",
  date: Date = new Date(),
  description: string = "",
  keyImage: number = 0,
  files: UploadFile[] = []
): UploadStack => ({
  id: id,
  type: type,
  dicom: dicom,
  study: study,
  modality: modality,
  date: date,
  description: description,
  keyImage: keyImage,
  files: files,
});

/**
 * Append an upload file and update the stack from the dataset.
 * @param stack The upload stack.
 * @param file The file to be appended.
 * @param dataset The DICOM dataset of the file.
 */
export const updateUploadStack = (
  stack: UploadStack,
  file: File,
  dataset: any
): string => {
  if (stack === undefined)
    throw new Error("invalid series: internal error");

  // SOP Instance UID (required)
  const instuid: string = stringOrEmpty(dataset.string("x00080018"));
  if (instuid.length === 0)
    throw new Error("invalid DICOM Object: missing SOP Instance UID");

  // check file is not in stack already
  if (stack.files.some((file) => file["inst"] === instuid)) return instuid;

  // add file to stack
  stack.files.push(Object.assign(file, { id: uuid(), inst: instuid }));

  // update stack properties (optional)
  updateDescription(stack, dataset);
  updateModality(stack, dataset);
  updateDate(stack, dataset);

  // done
  return instuid;
};

/**
 * Get the thumbnail image of the stack using the key image.
 * @param stack The upload stack.
 */
export const getUploadStackThumbnail = (
  stack: UploadStack,
  wado: any,
  patient?: Patient,
): Thumbnail | undefined => {
  // get key image
  const file = stack.files[stack.keyImage];

  // some checks
  if (file === undefined) return undefined;

  // get object URL of key image
  const preview = getUploadFileObjectURL(file);
  if (preview === undefined) return undefined;

  //  get study object for thumb
  const study = patientStudy(stack.study, patient);

  // create thumbnail
  const thumbnail = createThumbnail(
    stack.id,
    file,
    study,
    stack.description,
    stack.type,
    stack.files.length,
    stack.dicom,
    preview
  );

  // special case: DICOM
  if (thumbnail.dicom)
    thumbnail.url = wado.wadouri.fileManager.add(thumbnail.file);

   return thumbnail;
};

const containsAny = (stack: UploadStack, fileIds: string[]): boolean => {
  return stack.files.some(file => fileIds.some(id => file.id === id));
}

// returns all stacks that contain (at least) one of the file IDs
export const findNonDicomUploadStacksForFiles = (stacks: UploadStack[], fileIds: string[]): UploadStack[] => {
  const result = new Set<UploadStack>();
  for (const stack of stacks){
    if (containsAny(stack, fileIds) && !stack.dicom) {
      result.add(stack);
    }
  }
  // can't use spread operator here (gives error TS2569 for ES5 target)
  return Array.from(result);
};

/////////////////////////////////////////////////

const updateDescription = (stack: UploadStack, dataset: any) => {
  if (stack.description.length === 0) {
    stack.description = stringOrEmpty(dataset.string("x0008103e"));
  }
};

const updateModality = (stack: UploadStack, dataset: any) => {
  if (stack.modality.length === 0) {
    stack.modality = stringOrEmpty(dataset.string("x00080060"));
  }
};

const updateDate = (stack: UploadStack, dataset: any) => {
  const date = moment(dateFromDicomString(stringOrEmpty(dataset.string("x00080021")))).toDate();
  // only set if new date if older
  if (date && date < stack.date) stack.date = date;
};
